import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtDate, fmtDist, fmtDur } from "@/lib/api";
import { RouteOverviewMap, WARN_COLORS, WARN_LETTER } from "@/components/RouteOverviewMap";
import { SignaturePad } from "@/components/SignaturePad";
import { Navigation, Warehouse, Check, X, Undo2, Camera, PenLine, TrafficCone, Trash2 } from "lucide-react";

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "");

const compressImage = (file) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => resolve(b || file), "image/jpeg", 0.72);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

export default function DriverRoutePage() {
  const { token } = useParams();
  const [route, setRoute] = useState(null);
  const [error, setError] = useState(false);
  const [failFor, setFailFor] = useState(null);
  const [failReason, setFailReason] = useState("");
  const [busy, setBusy] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [sigFor, setSigFor] = useState(null);
  const [proofBusy, setProofBusy] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const photoFor = useRef(null);
  const photoInput = useRef(null);
  const lastSent = useRef(0);

  useEffect(() => {
    api.get(`/public/routes/${token}`).then((r) => setRoute(r.data)).catch(() => setError(true));
    api.get(`/public/routes/${token}/traffic`).then((r) => setTraffic(r.data)).catch(() => {});
    api.get(`/public/routes/${token}/proofs`).then((r) => setProofs(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setMyPos(p);
        if (Date.now() - lastSent.current > 20000) {
          lastSent.current = Date.now();
          api.post(`/public/routes/${token}/location`, { lat: p[0], lon: p[1] }).catch(() => {});
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [token]);

  if (error) return <div className="min-h-screen bg-neutral-100 text-neutral-500 flex items-center justify-center px-6 text-center">Route link not found. Ask your transport manager for a new link.</div>;
  if (!route) return <div className="min-h-screen bg-neutral-100 text-neutral-500 flex items-center justify-center">Loading route…</div>;

  const navUrl = (t) =>
    t && t.lat != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${t.lat},${t.lon}&travelmode=driving&dir_action=navigate`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(t?.postcode || t || "")}&travelmode=driving&dir_action=navigate`;
  const proofUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/public/routes/${token}/proofs/${id}`;

  const setStatus = async (storeId, status, reason) => {
    setBusy(storeId);
    try {
      const { data } = await api.post(`/public/routes/${token}/stops/${storeId}/status`, { status, reason: reason || null });
      setRoute(data);
      setFailFor(null);
      setFailReason("");
    } catch {
      toast.error("Could not update — check your signal and try again");
    } finally {
      setBusy(null);
    }
  };

  const reloadProofs = () => api.get(`/public/routes/${token}/proofs`).then((r) => setProofs(r.data)).catch(() => {});

  const uploadProof = async (storeId, kind, blob, filename) => {
    setProofBusy(storeId);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", blob, filename);
      await api.post(`/public/routes/${token}/stops/${storeId}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await reloadProofs();
      toast.success(kind === "photo" ? "Photo saved" : "Signature saved");
    } catch {
      toast.error("Upload failed — try again");
    } finally {
      setProofBusy(null);
    }
  };

  const onPhotoPicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !photoFor.current) return;
    const blob = await compressImage(f);
    uploadProof(photoFor.current, "photo", blob, "proof.jpg");
  };

  const deleteProof = async (podId) => {
    await api.delete(`/public/routes/${token}/proofs/${podId}`);
    reloadProofs();
  };

  const onSignatureSave = async (dataUrl) => {
    const storeId = sigFor;
    setSigFor(null);
    const blob = await (await fetch(dataUrl)).blob();
    uploadProof(storeId, "signature", blob, "signature.png");
  };

  const delivered = route.stops.filter((s) => s.delivery_status === "delivered").length;
  const failed = route.stops.filter((s) => s.delivery_status === "failed").length;
  const delayS = traffic?.live?.delay_s || 0;
  const warnings = traffic?.incidents || [];

  return (
    <div className="min-h-screen bg-neutral-100 text-black pb-12" data-testid="driver-route-page">
      <input ref={photoInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhotoPicked} />
      <SignaturePad open={!!sigFor} onClose={() => setSigFor(null)} onSave={onSignatureSave} title="CUSTOMER SIGNATURE" />

      <div className="bg-black text-white px-5 py-6">
        <div className="font-heading font-black text-xl tracking-tighter">
          LEO'S<span className="text-[#FF3B30]">FLEET</span> <span className="text-neutral-500 font-normal text-sm">· Route sheet</span>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="bg-[#FFD60A] text-black font-mono font-black text-lg px-3 py-1" data-testid="driver-route-reg">{route.vehicle_reg}</span>
          <div>
            <div className="font-bold">{route.driver_name || "Driver"}</div>
            <div className="text-xs text-neutral-400">
              {fmtDate(route.date)} · {route.stops.length} stops · {fmtDist(route.total_distance_m)} · {fmtDur(route.total_duration_s)}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-[11px] uppercase tracking-widest font-bold mb-1.5">
            <span data-testid="driver-progress-label" className="text-[#32D74B]">{delivered}/{route.stops.length} delivered</span>
            {failed > 0 && <span className="text-[#FF3B30]">{failed} failed</span>}
          </div>
          <div className="h-1.5 bg-neutral-800">
            <div className="h-full bg-[#32D74B] transition-all duration-300" style={{ width: `${(delivered / route.stops.length) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="px-4 mt-4 space-y-3 max-w-lg mx-auto">
        {traffic && (traffic.live?.travel_time_s != null || warnings.length > 0) && (
          <div className="bg-white border border-neutral-200 p-4" data-testid="driver-traffic-panel">
            <div className="flex items-center gap-2 uppercase text-[10px] tracking-[0.2em] font-bold text-neutral-500 mb-2">
              <TrafficCone size={13} className="text-[#FF3B30]" /> Live traffic
            </div>
            {traffic.live?.travel_time_s != null && (
              <div className="text-sm" data-testid="driver-traffic-eta">
                <span className="font-bold">{fmtDur(traffic.live.travel_time_s)}</span> drive in current traffic
                {delayS > 60 ? (
                  <span className={`ml-1.5 font-bold ${delayS >= 900 ? "text-[#FF3B30]" : "text-amber-600"}`}>+{Math.round(delayS / 60)} min delay</span>
                ) : (
                  <span className="ml-1.5 font-bold text-emerald-600">no significant delay</span>
                )}
              </div>
            )}
            {warnings.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {warnings.slice(0, 6).map((inc, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs" data-testid={`driver-traffic-warning-${i}`}>
                    <span className="mt-0.5 w-4 h-4 flex items-center justify-center text-white font-mono font-bold text-[9px] shrink-0 rounded-sm" style={{ background: WARN_COLORS[inc.icon_category] || "#f59e0b" }}>
                      {WARN_LETTER[inc.icon_category] || "!"}
                    </span>
                    <span className="min-w-0 text-neutral-700">
                      <span className="font-bold">{inc.category}</span>
                      {inc.road && <span className="font-mono"> {inc.road}</span>}
                      {inc.from && <span className="text-neutral-500"> — {inc.from}{inc.to ? ` → ${inc.to}` : ""}</span>}
                      {inc.delay_s ? <span className="text-[#FF3B30] font-bold"> +{Math.round(inc.delay_s / 60)}m</span> : null}
                    </span>
                  </div>
                ))}
                {warnings.length > 6 && <div className="text-[10px] uppercase tracking-widest text-neutral-400">+{warnings.length - 6} more on the map below</div>}
              </div>
            )}
          </div>
        )}

        <div className="bg-white border border-neutral-200 p-4 flex items-center gap-3">
          <Warehouse size={18} className="shrink-0" />
          <div>
            <div className="font-bold text-sm">Start: {route.yard?.name || "Yard"}</div>
            <div className="text-xs text-neutral-500 font-mono">{route.yard?.postcode}</div>
          </div>
        </div>

        {route.stops.map((s) => {
          const stopProofs = proofs.filter((p) => p.store_id === s.store_id);
          return (
          <div key={s.store_id} data-testid={`driver-stop-${s.order}`} className={`bg-white border p-4 ${s.delivery_status === "delivered" ? "border-emerald-300" : s.delivery_status === "failed" ? "border-red-300" : "border-neutral-200"}`}>
            <div className="flex items-center gap-3">
              <span className={`w-9 h-9 flex items-center justify-center text-white font-mono font-black shrink-0 rounded-full ${s.delivery_status === "delivered" ? "bg-emerald-600" : "bg-[#FF3B30]"}`}>
                {s.delivery_status === "delivered" ? <Check size={18} /> : s.order}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-bold leading-tight">{s.name}</div>
                <div className="text-xs text-neutral-500 font-mono mt-0.5">{s.postcode} · +{fmtDist(s.leg_distance_m)} · {fmtDur(s.leg_duration_s)}</div>
              </div>
              <a
                data-testid={`navigate-stop-${s.order}`}
                href={navUrl(s)}
                target="_blank"
                rel="noreferrer"
                className="bg-black text-white px-3 py-2.5 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1.5 shrink-0"
              >
                <Navigation size={13} /> Go
              </a>
            </div>

            {s.delivery_status === "delivered" && (
              <>
                <div className="mt-3 flex items-center justify-between bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid={`stop-delivered-${s.order}`}>
                  <span className="text-emerald-700 text-sm font-bold inline-flex items-center gap-1.5"><Check size={15} /> Delivered {fmtTime(s.delivery_time)}</span>
                  <button data-testid={`undo-stop-${s.order}`} onClick={() => setStatus(s.store_id, "pending")} className="text-[10px] text-neutral-400 hover:text-neutral-600 inline-flex items-center gap-1 uppercase tracking-widest font-bold"><Undo2 size={12} /> Undo</button>
                </div>
                <div className="mt-2" data-testid={`proof-section-${s.order}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      data-testid={`add-photo-${s.order}`}
                      disabled={proofBusy === s.store_id}
                      onClick={() => { photoFor.current = s.store_id; photoInput.current?.click(); }}
                      className="border border-neutral-300 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-neutral-600 inline-flex items-center gap-1.5"
                    >
                      <Camera size={13} /> {proofBusy === s.store_id ? "Saving…" : "Photo"}
                    </button>
                    <button
                      data-testid={`add-signature-${s.order}`}
                      disabled={proofBusy === s.store_id}
                      onClick={() => setSigFor(s.store_id)}
                      className="border border-neutral-300 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-neutral-600 inline-flex items-center gap-1.5"
                    >
                      <PenLine size={13} /> Sign
                    </button>
                    {stopProofs.map((p) => (
                      <span key={p.id} className="relative inline-block">
                        <a href={proofUrl(p.id)} target="_blank" rel="noreferrer" data-testid={`proof-thumb-${p.id}`}>
                          <img src={proofUrl(p.id)} alt={p.kind} className="w-12 h-12 object-cover border border-neutral-300" />
                        </a>
                        <button data-testid={`delete-proof-${p.id}`} onClick={() => deleteProof(p.id)} className="absolute -top-1.5 -right-1.5 bg-white border border-neutral-300 rounded-full p-0.5 text-neutral-500"><Trash2 size={10} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
            {s.delivery_status === "failed" && (
              <div className="mt-3 flex items-center justify-between bg-red-50 border border-red-200 px-3 py-2" data-testid={`stop-failed-${s.order}`}>
                <span className="text-red-700 text-sm font-bold inline-flex items-center gap-1.5"><X size={15} /> Not delivered{s.delivery_reason ? ` — ${s.delivery_reason}` : ""}</span>
                <button data-testid={`undo-stop-${s.order}`} onClick={() => setStatus(s.store_id, "pending")} className="text-[10px] text-neutral-400 hover:text-neutral-600 inline-flex items-center gap-1 uppercase tracking-widest font-bold"><Undo2 size={12} /> Undo</button>
              </div>
            )}
            {!s.delivery_status && (
              failFor === s.store_id ? (
                <div className="mt-3 space-y-2" data-testid={`fail-form-${s.order}`}>
                  <input
                    data-testid={`fail-reason-input-${s.order}`}
                    value={failReason}
                    onChange={(e) => setFailReason(e.target.value)}
                    placeholder="Reason (e.g. shop closed)"
                    className="w-full border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-black"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid={`confirm-failed-${s.order}`} disabled={busy === s.store_id} onClick={() => setStatus(s.store_id, "failed", failReason)} className="bg-[#FF3B30] text-white py-2.5 text-xs font-bold uppercase tracking-widest">Confirm failed</button>
                    <button data-testid={`cancel-failed-${s.order}`} onClick={() => { setFailFor(null); setFailReason(""); }} className="border border-neutral-300 py-2.5 text-xs font-bold uppercase tracking-widest text-neutral-600">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button data-testid={`mark-delivered-${s.order}`} disabled={busy === s.store_id} onClick={() => setStatus(s.store_id, "delivered")} className="bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 text-xs font-bold uppercase tracking-widest inline-flex items-center justify-center gap-1.5">
                    <Check size={14} /> {busy === s.store_id ? "…" : "Delivered"}
                  </button>
                  <button data-testid={`mark-failed-${s.order}`} onClick={() => { setFailFor(s.store_id); setFailReason(""); }} className="border border-[#FF3B30] text-[#FF3B30] py-2.5 text-xs font-bold uppercase tracking-widest inline-flex items-center justify-center gap-1.5">
                    <X size={14} /> Failed
                  </button>
                </div>
              )
            )}
          </div>
          );
        })}

        <div className="bg-white border border-neutral-200 p-4 flex items-center gap-3">
          <Warehouse size={18} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">Return to {route.yard?.name || "Yard"}</div>
            <div className="text-xs text-neutral-500 font-mono">{route.yard?.postcode} · +{fmtDist(route.return_leg?.distance_m || 0)} · {fmtDur(route.return_leg?.duration_s || 0)}</div>
          </div>
          <a href={navUrl(route.yard)} target="_blank" rel="noreferrer" className="bg-black text-white px-3 py-2.5 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1.5 shrink-0">
            <Navigation size={13} /> Go
          </a>
        </div>

        {route.geometry?.length > 1 && (
          <div data-testid="driver-route-map">
            <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-neutral-500 mb-2 mt-2">Route overview {warnings.length > 0 ? "· traffic warnings marked" : ""}</div>
            <div className="border border-neutral-200 overflow-hidden bg-white">
              <RouteOverviewMap route={route} height={300} incidents={warnings} jamSegments={traffic?.jam_segments || []} driverPos={myPos} showTraffic interactive />
            </div>
          </div>
        )}

        <div className="text-center text-[10px] uppercase tracking-widest text-neutral-400 pt-2">Leo's Fleet Transport Ops</div>
      </div>
    </div>
  );
}
